import { useCallback, useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import PageHeader from "@/components/PageHeader";

export default function TadaConfigPage() {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/tada/config");
    setCfg(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (patch) => {
    setBusy(true);
    try {
      const { data } = await api.patch("/tada/config", patch);
      setCfg(data);
      toast.success("Settings updated");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (!cfg) return <div className="text-sm text-neutral-500">Loading…</div>;

  return (
    <div className="space-y-4 max-w-2xl" data-testid="tada-config-page">
      <PageHeader
        breadcrumbs={[{ label: "Admin", to: "/" }, { label: "Masters" }, { label: "TA/DA settings" }]}
        title="TA/DA settings"
        subtitle="How far back a field user can file a TA/DA claim."
        favorite={{ key: "admin.tada-config", type: "screen", label: "TA/DA settings", route: "/admin/tada-config" }}
        testid="tadaconfigpage-page-header"
      />

      <div>
        <h1 className="text-2xl font-bold text-neutral-900">TA/DA settings</h1>
        <p className="text-sm text-neutral-500">
          A claim always requires that day's attendance to exist — Day End itself is
          separately gated on a TA/DA claim (or expense) already being submitted for
          today, so the two can't require each other. This setting only controls how
          many days back the claim_date itself may be.
        </p>
      </div>

      <Card className="border-neutral-200 rounded-2xl">
        <CardContent className="p-5 space-y-5">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <div className="text-sm font-semibold text-neutral-900">Max backdate (days)</div>
              <div className="text-xs text-neutral-500 mt-0.5 max-w-md">
                0 = today only. 1 = today or yesterday. Set higher to let field
                users catch up on TA/DA claims for older missed days.
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Input type="number" min={0} max={90} className="w-24"
                     defaultValue={cfg.max_backdate_days ?? (cfg.allow_next_day_backfill ? 1 : 0)}
                     onBlur={(e) => save({ max_backdate_days: Number(e.target.value) })}
                     disabled={busy}
                     data-testid="tada-cfg-max-backdate-days" />
              <span className="text-sm text-neutral-500">days</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
